import { Box, Grid, Stack } from "@mui/material";
import { Link } from "react-router-dom";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import HeaderContainer from "styles/components/headerContainer";
import theme from "assets/theme";
import useMediaQueries from "utils/mediaQueries.utils";
import { replaceKeywordWithLink } from "utils/helpers/replaceKeywordWithLink";

const defaultInsuranceLogos = [
  {
    "src": "https://almadelic-assets.s3.us-east-2.amazonaws.com/insuranceLogos/Anthem.webp",
    "alt": "Anthem Blue Cross Blue Shield Insurance",
    "url": "/insurance-options/anthem",
    "name": "Anthem",
    "cssImageProps": {
      "transform": "scale(1.1)",
    },
  },
  {
    "src": "https://almadelic-assets.s3.us-east-2.amazonaws.com/insuranceLogos/Aetna.webp",
    "alt": "Aetna Insurance",
    "url": "/insurance-options/aetna",
    "name": "Aetna",
    "cssImageProps": {
      "transform": "scale(.9)",
    },
  },
  {
    "src": "https://almadelic-assets.s3.us-east-2.amazonaws.com/insuranceLogos/Cigna.webp",
    "alt": "Cigna Healthcare",
    "url": "/insurance-options/cigna",
    "name": "Cigna",
  },
  {
    "src": "https://almadelic-assets.s3.us-east-2.amazonaws.com/insuranceLogos/oscarHealth.svg",
    "alt": "Oscar Health",
    "url": "/insurance-options/oscar",
    "name": "Oscar",
    "cssImageProps": {
      "transform": "scale(.7)",
    },
  },
  {
    "src": "https://almadelic-assets.s3.us-east-2.amazonaws.com/insuranceLogos/unitedHealthcare2.webp",
    "alt": "United Healthcare",
    "url": "/insurance-options/united-healthcare",
    "name": "United Healthcare",
    "cssImageProps": {
      "transform": "scale(.8)",
    },
  },
  {
    "src": "https://almadelic-assets.s3.us-east-2.amazonaws.com/insuranceLogos/oxford.png",
    "alt": "Oxford Health",
    "url": "/insurance-options/oxford",
    "name": "Oxford Health",
    "cssImageProps": {
      "objectFit": "cover",
      "transform": { xs: "scale(1.5)", md: "scale(1.6)" },
      "marginLeft": { xs: "0", md: "2rem" },
    },
  },
];

const TitleComponent = ({ title, textColor, textAlign }) => {
  return <HeaderContainer title={title} textColor={textColor} component="h2" variant="h2" cssProps={{ textAlign, color: textColor }} />;
};

const DescriptionComponent = ({ descriptionArray, textColor, textAlign, isLg, isBold }) => {
  return (
    <>
      {descriptionArray.map((desc, index) => {
        // if desc has isBold true, then bold the text
        if (desc?.isBold) {
          desc.text = <span style={{ fontWeight: "bold" }}>{desc.text}</span>;
        }

        // Check if desc is an object with text property
        if (typeof desc === "object" && desc !== null && desc.text) {
          // Handle object with text property and optional link
          let content = desc.text;

          if (desc.link && desc.highlightText && desc.highlightText.length > 0) {
            const keywords = desc.highlightText.map((text) => ({
              keyword: text,
              embeddedLink: desc.link,
              linkColor: textColor,
            }));
            content = replaceKeywordWithLink(desc.text, keywords, null, null, "routerLink");
          } else if (desc.link) {
            const keywords = [
              {
                keyword: desc.text,
                embeddedLink: desc.link,
                linkColor: textColor,
              },
            ];
            content = replaceKeywordWithLink(desc.text, keywords, null, null, "routerLink");
          }

          return (
            <MKTypography
              key={index}
              variant={isLg ? "body1" : "body2"}
              component="p"
              sx={{
                textAlign,
                maxWidth: "800px",
                color: textColor,
                lineHeight: 1.6,
                marginTop: "1rem !important",
              }}
            >
              {content}
            </MKTypography>
          );
        } else {
          // Handle string values
          return (
            <MKTypography
              key={index}
              variant={isLg ? "body1" : "body2"}
              component="p"
              sx={{
                textAlign,
                maxWidth: "800px",
                color: textColor,
                lineHeight: 1.6,
                marginTop: ".5rem !important",
              }}
            >
              {desc}
            </MKTypography>
          );
        }
      })}
    </>
  );
};

const InsuranceLogosComponent = ({ insuranceLogosToRender, colorWrapper }) => {
  return (
    <>
      {insuranceLogosToRender.length > 0 && (
        <Box sx={{ width: "100%", margin: "2rem 0" }}>
          <Grid container spacing={{ xs: 4, md: 4 }} justifyContent={{ xs: "flex-start", md: "center" }}>
            {insuranceLogosToRender.map((logo, index) => (
              <Grid item xs={4} sm={4} md={3} lg={2} key={index}>
                <Box
                  sx={{
                    justifyContent: "center",
                    alignItems: "center",
                    display: "flex",
                  }}
                >
                  {logo.url ? (
                    <Link
                      disabled
                      to={logo.url}
                      style={{
                        textDecoration: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        height: "100%",
                      }}
                      aria-label={`View ${logo.alt} insurance information`}
                    >
                      <Box
                        component="img"
                        src={logo.src || logo.imageUrl}
                        alt={logo.alt}
                        sx={{
                          width: { xs: "100px", md: "150px" },
                          height: { xs: "30px", md: "45px" },
                          objectFit: "contain",
                          filter: colorWrapper ? "brightness(0) invert(1)" : "none",
                          opacity: colorWrapper ? 0.9 : 1,
                          ...(logo.cssImageProps || {}),
                          "&:hover": {
                            filter: "drop-shadow(0 12px 24px rgba(0, 255, 204, .5)) brightness(0) invert(1)",
                          },
                          transition: "all 0.3s ease-in-out",
                        }}
                      />
                    </Link>
                  ) : (
                    <Box
                      component="img"
                      src={logo.src || logo.imageUrl}
                      alt={logo.alt}
                      sx={{
                        width: { xs: "100px", md: "150px" },
                        height: { xs: "30px", md: "45px" },
                        objectFit: "contain",
                        filter: colorWrapper ? "brightness(0) invert(1)" : "none",
                        opacity: colorWrapper ? 0.9 : 1,
                        ...(logo.cssImageProps || {}),
                        "&:hover": {
                          filter: "drop-shadow(0 12px 24px rgba(0, 255, 204, .5)) brightness(0) invert(1)",
                        },
                        transition: "all 0.3s ease-in-out",
                      }}
                    />
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </>
  );
};

const CTAComponent = ({ cta }) => {
  return (
    <>
      {cta && (
        <MKButton
          component={Link}
          to={cta.link}
          variant="contained"
          color="secondary"
          size="large"
          sx={{
            padding: { xs: "0.5rem 1rem", md: "0.5rem 1.5rem" },
            marginTop: "2.5rem !important",
            fontWeight: "bold",
            textTransform: "uppercase",
          }}
        >
          {cta.text}
        </MKButton>
      )}
    </>
  );
};

const InsuranceSection = ({
  title,
  description,
  cta,
  insuranceLogos = [],
  colorWrapper = false,
  textAlign = "center",
  maxWidth = "1100px",
  insuranceSectionLayout = "default",
  subTitle = "",
  isBold = false,
}) => {
  const descriptionArray = Array.isArray(description) ? description : [description];
  const insuranceLogosToRender = insuranceLogos.length > 0 ? insuranceLogos : defaultInsuranceLogos;

  const textColor = colorWrapper ? theme.palette.common.white : theme.palette.text.primary;
  const { isLg } = useMediaQueries();
  return (
    <Box
      sx={{
        padding: { xs: "2rem 1rem", md: "4rem 2rem" },
        display: "flex",
        justifyContent: "center",
        width: "100%",
        alignItems: "center",
      }}
    >
      <Box sx={{ maxWidth, width: "100%" }}>
        <Stack spacing={3} alignItems="center">
          {insuranceSectionLayout === "default" ? (
            <>
              <TitleComponent title={title} textColor={textColor} textAlign={textAlign} />
              {subTitle && (
                <HeaderContainer title={subTitle} textColor={textColor} component="h3" variant="h3" cssProps={{ textAlign, color: textColor }} />
              )}
              <DescriptionComponent descriptionArray={descriptionArray} textColor={textColor} textAlign={textAlign} isLg={isLg} isBold={isBold} />
              <InsuranceLogosComponent insuranceLogosToRender={insuranceLogosToRender} colorWrapper={colorWrapper} />
              <CTAComponent cta={cta} />
            </>
          ) : (
            <>
              <TitleComponent title={title} textColor={textColor} textAlign={textAlign} />
              <InsuranceLogosComponent insuranceLogosToRender={insuranceLogosToRender} colorWrapper={colorWrapper} />
              <DescriptionComponent descriptionArray={descriptionArray} textColor={textColor} textAlign={textAlign} isLg={isLg} isBold={isBold} />
              <CTAComponent cta={cta} />
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default InsuranceSection;
